import { Injectable } from '@angular/core';
import { ReasonForInsurance, UnsupportedCase } from '../../classes/UnsupportedUseCases';
import { Exchange1035FundingPurpose, ModalType, ReplacementCompanies } from '../../../js/interfaces/CoverpathTypes';
import { ValidationService } from '../validation/validation.service';
import { ModalService } from '../modal/modal.service';
import { ApplicationService } from '../application/application.service';
import { WindowService } from '../../../../common/angular-shared/services/window/window.service';
import { Product, RiderConstants } from '../../../../common/js/static/Product';
import { Policy } from '../../../../common/js/customer/Policy';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { ExistingPolicy } from '../../../../common/js/customer/ExistingPolicy';
import _ from 'lodash';
import { Utils } from '../../../../common/js/Utils';
import { getOtherCompanyReplacementWarning } from '../../components/modals/partials/generic/generic.configs';
import { CoverpathBasePolicy } from '../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { MMDirectPolicy } from '../../../classes/mmdirect_subclasses/MMDirectPolicy';
import { Person } from '../../../../common/js/customer/Person';
import { Assumptions } from '../../../../common/js/static/Assumptions';
import { PayerTypeOptions } from '../../../../common/js/interfaces/CommonTypes';
import { CIRValidationUtils } from '@hit/validation-utils';
import { ValidationStatic } from '../validation/validation-static';

@Injectable()
export class UnsupportedUseCasesService {
  isNwmReplacementsEnabled: boolean = false;

  //private windowService: WindowService;
  constructor(
    private modalService: ModalService,
    private appService: ApplicationService,
    private windowService: WindowService
  ) {
    // this.windowService = _windowService;
    this.isNwmReplacementsEnabled = this.windowService.nativeWindow.controller.isNwmReplacementsEnabled;
  }

  async isUnsupportedUseCase(prop: string, value): Promise<boolean> {
    const simplePropertyName = prop.split('.').slice(-1).join();

    const application = this.appService.getValue('customer.application');
    const primaryPolicy: CoverpathBasePolicy = application.primaryPolicy;
    let castedPolicy: CoverpathPolicy | MMDirectPolicy;
    if (primaryPolicy.isCoverpathPolicy()) {
      castedPolicy = <CoverpathPolicy>primaryPolicy;
    } else if (primaryPolicy.isMMDirectPolicy()) {
      castedPolicy = <MMDirectPolicy>primaryPolicy;
    }
    const insuredType: string = primaryPolicy.insuredType;
    const ownerType: string = primaryPolicy.ownerType;
    const isPartOfJointApp: boolean = primaryPolicy.isPartOfJointApp();
    const ownerDifferentFromInsured: boolean = !primaryPolicy.isOwnerSameAsInsured();
    const useNewReplacementLogic = this.windowService.nativeWindow.controller.enableNewReplacementFeature; // new feature flag for later release.
    const alir1035NoPremium = this.windowService.nativeWindow.controller.alir1035NoPremium;
    const alir1035Premium = this.windowService.nativeWindow.controller.alir1035Premium;
    const isCoverpathNYEnabled: boolean = this.windowService.nativeWindow.controller.isCoverpathNYEnabled;

    let unsupportedCase: UnsupportedCase;
    let unsupportedCaseData: string;

    switch (simplePropertyName) {
      // need this for legacy applications that had term vs. whole question in app.
      case 'productType':
        unsupportedCase = value === 'WholeLife' ? UnsupportedCase.productType : undefined;
        break;
      case 'SSN':
        unsupportedCase = await this.validateSSN(primaryPolicy, prop, value);
        break;
      case 'noSSN':
        if (value) {
          unsupportedCase = prop.toLowerCase().includes('owner')
            ? UnsupportedCase.noSSNOwner
            : UnsupportedCase.noSSNInsured;
        }
        break;
      case 'noEIN':
        unsupportedCase = value ? UnsupportedCase.noEIN : undefined;
        break;
      case 'noTIN':
        if (value && prop.indexOf('primaryBeneficiary') < 0 && prop.indexOf('contingentBeneficiary') < 0) {
          unsupportedCase = UnsupportedCase.noTIN;
        }
        break;
      case 'isUSEntity':
        unsupportedCase = value === 'false' ? UnsupportedCase.isUSEntity : undefined;
        break;
      case 'reasonForInsurance':
        switch (value) {
          case ReasonForInsurance.other:
            unsupportedCase = UnsupportedCase.other;
            break;
          case ReasonForInsurance.none:
            unsupportedCase = UnsupportedCase.none;
            break;
          case ReasonForInsurance.unknown:
            unsupportedCase = UnsupportedCase.unknown;
            break;
          case ReasonForInsurance.investment:
            unsupportedCase = UnsupportedCase.investment;
            break;
        }
        break;
      case 'residencyStatus':
        const isAllowedStatus = (value) =>
          ['citizen', 'residentAlien', 'nonResidentAlien', 'none'].indexOf(value) !== -1;

        // always block when 2018 Coverpath filing (Non-ICC states) and residencyStatus is not "U.S. Citizen and reside in the U.S."
        if (!this.appService.appState.controller.customer.isCoverpath2019MetadataEnabled() && value !== 'citizen') {
          unsupportedCase = UnsupportedCase.citizen;
          break;
        }

        // block any non-us citizen insured from having $1MM of total coverage
        if (!isAllowedStatus(value)) {
          // always block when residencyStatus is not in the allowed status list
          unsupportedCase = UnsupportedCase.citizen;
          break;
        } else if (value !== 'citizen') {
          /**
           * if residencyStatus is "Non-U.S. Citizen and reside in the U.S. as a Permanent Resident (Green Card Holder)"
           * check if the insured's residency is being changed and if it is, the total coverage cannot exceed $1MM
           */
          const rtrAmount: number = primaryPolicy.rtrRiderAmount || 0;
          unsupportedCase =
            prop.includes('insured') &&
            this.appService.appState.controller.customer.application.getTotalFaceAmount() + rtrAmount > 1000000
              ? UnsupportedCase.nonUSCitizenMaxFace
              : undefined;
          break;
        }
      case 'faceAmount': {
        let otherPolicyFaceAmount: number = 0;
        if (prop.includes('primaryPolicy')) {
          // primary policy face amount changed so get secondary policy face amount (if secondary policy exists)
          otherPolicyFaceAmount = this.appService.appState.controller.customer.application.secondaryPolicy
            ? this.appService.appState.controller.customer.application.secondaryPolicy.faceAmount
            : 0;
        } else if (prop.includes('secondaryPolicy')) {
          // secondary policy face amount changed so get primary policy face amount
          otherPolicyFaceAmount = this.appService.appState.controller.customer.application.primaryPolicy.faceAmount;
        }
        // strip '$' and ',' from value
        const strippedValue = value.replace('$', '').replace(/,/g, '');
        /**
         * if owner is of type entity, the maximum face amount is determined in answer from owner section
         **/
        const entity = this.appService.appState.controller.customer.application.primaryPolicy.trustOrOtherEntity;
        if (entity && entity.maxFaceAmt) {
          if (parseFloat(strippedValue) + otherPolicyFaceAmount > entity.maxFaceAmt) {
            unsupportedCase = UnsupportedCase.entityFaceAmountExceeded;
            break;
          }
        }

        /**
         * Need to block Non-US Insured from having over $1MM of total coverage
         */
        const residencyStatus = primaryPolicy.insured.person.residencyStatus;
        if (residencyStatus) {
          unsupportedCase =
            residencyStatus !== 'citizen' && parseFloat(strippedValue) + otherPolicyFaceAmount > 1000000
              ? UnsupportedCase.nonUSCitizenMaxFace
              : undefined;
        }

        /**
         * Creating non-generic unsupported modal text for CIR
         */
        if (primaryPolicy.isWholeLifePolicy() && (primaryPolicy as CoverpathPolicy).hasCirRider) {
          if (!CIRValidationUtils.isValidFaceAmount(strippedValue).isValid) {
            unsupportedCase = UnsupportedCase.cirMinimumFaceAmount;
          } else if ((primaryPolicy as CoverpathPolicy).cirRiderAmount) {
            const policy = castedPolicy as CoverpathPolicy;
            const cirValidation = ValidationService.validateCirAmount(policy.cirRiderAmount, parseFloat(strippedValue));
            if (!cirValidation.result) {
              // Need to modify the calculation if CIR eligibility criteria changed in future
              let minFaceAmount = Math.ceil(policy.cirRiderAmount / RiderConstants.cir.highFaceAmountCIRMaxPercent);
              if (minFaceAmount < RiderConstants.cir.minFaceAmount) {
                minFaceAmount = RiderConstants.cir.minFaceAmount;
              }

              const cirCompoundError = `Because this policy includes CIR with a ${Utils.formatCurrency(
                policy.cirRiderAmount,
                0
              )} base benefit pool, the minimum face amount is ${Utils.formatCurrency(
                minFaceAmount,
                0
              )}. Edit the CIR base benefit pool if you want to make this change.`;
              unsupportedCase = UnsupportedCase.cirCompoundError;
              unsupportedCaseData = cirCompoundError;
            }
          }
        }
        break;
      }
      case 'isFATCAResidencyStatus':
        unsupportedCase = ['false', 'none', 'nonResidentAlien', 'citizenOutsideUS'].includes(value)
          ? UnsupportedCase.citizen
          : undefined;
        break;
      case 'dob':
        //check INSURED age only
        if (!prop.match('insured.person.dob')) {
          break;
        }

        if (isPartOfJointApp || primaryPolicy.isWholeLifePolicy()) {
          // validate age for Primary Policy (Whole Life)
          unsupportedCase = this.validateAgeForWholeLife(primaryPolicy, value);
        }

        // TODO: HL-28195 Why are we overriding validation messages with a generic one.
        if (unsupportedCase) {
          unsupportedCase = UnsupportedCase.invalidAgeForProducts;
          break;
        }

        /**
         * Do not allow switching from a Juvenile DOB (< 18 years old) to a non Juvenile DOB (>= 18 years old)
         * or vice versa
         */
        const quoteDateOfBirth = this.appService.getValue('customer.currentQuote.dateOfBirth');
        const origDobIsJuvenile: boolean = ValidationService.isJuvenile(quoteDateOfBirth);
        const newDobIsJuvenile: boolean = ValidationService.isJuvenile(new Date(value));
        if (origDobIsJuvenile !== newDobIsJuvenile) {
          unsupportedCase = UnsupportedCase.juveToAdultOrAdultToJuve;
          break;
        }

        break;
      case 'state':
        //check OWNER state only if owner/insured different
        const addressProp: string = prop.replace('.state', '');
        const zipProp: string = addressProp + '.zip';
        const cityProp: string = addressProp + '.city';

        const isUnsupportedContractStateResult = this.isUnsupportedContractState(
          primaryPolicy.isTermLifePolicy(),
          isCoverpathNYEnabled,
          value
        );
        if (isUnsupportedContractStateResult.isUnsupportedContractState) {
          let isNewStateUnsupported: boolean = false;
          if ((!ownerDifferentFromInsured || !ownerType) && CoverpathUtils.isInsuredAddressProp(prop)) {
            // insured = owner and changing insured's state to unsupported value
            isNewStateUnsupported = true;
          } else if (ownerDifferentFromInsured && CoverpathUtils.isOwnerAddressProp(prop)) {
            // insured != owner and changing (one of the) owner's state to unsupported value
            isNewStateUnsupported = true;
          } else if (
            /**
             * joint ownership where "Who will be the Joint Owner?" != Someone Else (so need to assume "other" owner = insured)
             * and changing insured's state to unsupported value
             */
            primaryPolicy.isJointOwnership() &&
            primaryPolicy.isJointOwnerSomeoneElse !== 'true' &&
            CoverpathUtils.isInsuredAddressProp(prop)
          ) {
            isNewStateUnsupported = true;
          }
          if (isNewStateUnsupported) {
            unsupportedCase = isUnsupportedContractStateResult.unsupportedKey;
            this.appService.submitValueAndFireTriggers(zipProp, undefined);
            this.appService.submitValueAndFireTriggers(cityProp, undefined);
          }
        }
        break;
      case 'insuredState':
        const isUnsupportedInsuredStateResult = this.isUnsupportedContractState(
          primaryPolicy.isTermLifePolicy(),
          isCoverpathNYEnabled,
          value
        );
        if (isUnsupportedInsuredStateResult.isUnsupportedContractState) {
          unsupportedCase = isUnsupportedInsuredStateResult.unsupportedKey;
        }
        break;
      case 'premiumSource':
        unsupportedCase = value === 'true' ? UnsupportedCase.premium : undefined;
        break;
      case 'premiumSourceYesNoOther':
        unsupportedCase = value === 'loan' ? UnsupportedCase.premium : undefined;
        break;
      case 'hivPositiveWhichAddress':
        unsupportedCase = value === 'optout' ? UnsupportedCase.hiv : undefined;
        break;
      case 'collaterallyAssigned':
        if (!primaryPolicy.customer.isCoverpath2019MetadataEnabled()) {
          unsupportedCase = value === 'true' ? UnsupportedCase.collateral : undefined;
        }
        break;
      case 'economicIncentive':
        unsupportedCase = value === 'true' ? UnsupportedCase.economic : undefined;
        break;
      case 'replaceInforceApplication':
        const isMMD = primaryPolicy.getChannelCode() === Product.channels.MMDirect.code;

        if (isMMD && value === 'true') {
          unsupportedCase = UnsupportedCase.replacements;
        }

        if (useNewReplacementLogic && !primaryPolicy.customer.isCoverpath2019MetadataEnabled()) {
          if (value === 'true') {
            if (primaryPolicy.getChannelCode() === 'CAS') {
              // Replacement supported. Show replacement modal instead of unsupported modal
              this.activateReplacementModal();
              return false;
            } else {
              unsupportedCase = UnsupportedCase.replacements;
            }
          } else {
            unsupportedCase = undefined;
          }
        }

        if (value === 'true') {
          if (primaryPolicy.isWholeLifePolicy() && !this.windowService.nativeWindow.controller.is1035ExchangeEnabled) {
            this.activateALIR1035Modal();
          }
        }

        break;
      case 'cashValueTransfer':
        if (this.parseCurrency(value) !== 0) {
          if (castedPolicy.isTermLifePolicy()) {
            unsupportedCase = UnsupportedCase.termPolicyWith1035;
          } else if (!this.appService.appState.controller.is1035ExchangeEnabled) {
            unsupportedCase = UnsupportedCase.cashValueTransfer;
          } else if (castedPolicy.hasAlirScheduledPayments) {
            unsupportedCase = UnsupportedCase.scheduledPaymentWith1035;
          } else if (this.hasOtherCashValueReplacement(prop)) {
            unsupportedCase = UnsupportedCase.multipleCashValueReplacements;
          } else if (castedPolicy.trustOrOtherEntity && castedPolicy.trustOrOtherEntity.specificType === 'charity') {
            unsupportedCase = UnsupportedCase.alirOwnerExclusion;
          }
        }
        break;
      case 'replacementLoanAmount':
        if (this.parseCurrency(value) !== 0) {
          unsupportedCase = UnsupportedCase.replacementLoanAmount;
        }
        break;
      case 'face':
      case 'purpose':
      case 'type':
      case 'coverageStartDate':
      case 'otherCompanyName':
      case 'owner':
      case 'insured':
      case 'insuredName':
      case 'number':
      case 'companyName':
      case 'reimburseMethod': {
        if (!prop.includes('existingPolicies')) {
          break;
        }

        const existingInForcePolicy = this.getEntityFromProp(prop); // ExistingPolicies.existingInForcePolicyList[n]
        if (simplePropertyName === 'companyName' && value === 'other') {
          this.modalService.activate(
            getOtherCompanyReplacementWarning(
              (modalService: ModalService) => {
                modalService.deactivate();
              },
              (modalService: ModalService) => {
                this.appService.submitValueAndFireTriggers(prop, undefined);
                modalService.deactivate();
              }
            )
          );

          break;
        }

        // Check for unsupported internal replacements (i.e. current carrier is MassMutual or Northwestern Mutual)
        const internalUnsupportedCompanies = [];
        if (!this.isNwmReplacementsEnabled) {
          internalUnsupportedCompanies.push(ReplacementCompanies.NorthwesternMutual);
        }
        if (simplePropertyName === 'companyName' && internalUnsupportedCompanies.includes(value)) {
          const customerHasReplacementPolicies =
            Boolean(existingInForcePolicy) && Boolean(existingInForcePolicy.isReplacement);
          const customerHasReplacementApplications = Boolean(
            this.appService.getValue(
              'customer.application.primaryPolicy.ownerPerson.existingPolicies.replaceInforceApplication'
            )
          );

          if (customerHasReplacementPolicies || customerHasReplacementApplications) {
            if (value === ReplacementCompanies.NorthwesternMutual) {
              unsupportedCase = UnsupportedCase.replaceNorthwesternMutual;
            } else {
              unsupportedCase = UnsupportedCase.replaceCompanies;
            }
            break;
          }
        }

        if (
          existingInForcePolicy.companyName === 'MassMutual' &&
          simplePropertyName === 'reimburseMethod' &&
          value !== 'asCash'
        ) {
          unsupportedCase = UnsupportedCase.reimburseMethod;
          break;
        }

        if (_.isBoolean(existingInForcePolicy.isGroupPolicy)) {
          break;
        }
        const hasExistingInforcePolicies: boolean = this.appService.getValue(
          `customer.application.primaryPolicy.insured.existingPolicies.hasExistingInforcePolicies`
        );
        if (hasExistingInforcePolicies && !existingInForcePolicy.isReplacement) {
          break;
        }

        if (
          this.allFieldsCompletedForReplacementPolicy(existingInForcePolicy, simplePropertyName, value) &&
          !primaryPolicy.is2020CoverpathOrMMD()
        ) {
          this.activateGroupReplacementModal(existingInForcePolicy);
        }

        break;
      }
      case 'isReplacementFunds':
      case 'isReplacement':
        if (primaryPolicy.getChannelCode() === Product.channels.MMDirect.code && value === 'true') {
          unsupportedCase = UnsupportedCase.replacements;
        }

        if (useNewReplacementLogic && !this.appService.appState.controller.customer.isCoverpath2019MetadataEnabled()) {
          if (primaryPolicy.getChannelCode() === 'CAS') {
            break;
          } else {
            unsupportedCase = value === 'true' ? UnsupportedCase.replacements : undefined;
          }
        }
        // Check for unsupported internal replacements (i.e. current carrier is Northwestern Mutual)
        if (value === 'true') {
          const existingPolicy = this.getEntityFromProp(prop); // ExistingPolicies.existingInForcePolicyList[n]

          const internalUnsupportedCompanies = [];
          if (!this.isNwmReplacementsEnabled) {
            internalUnsupportedCompanies.push(ReplacementCompanies.NorthwesternMutual);
          }
          if (existingPolicy && internalUnsupportedCompanies.includes(existingPolicy.companyName)) {
            if (existingPolicy.companyName === ReplacementCompanies.NorthwesternMutual) {
              unsupportedCase = UnsupportedCase.replaceNorthwesternMutual;
            } else {
              unsupportedCase = UnsupportedCase.replaceCompanies;
            }
          } else if (
            primaryPolicy.isWholeLifePolicy() &&
            !this.windowService.nativeWindow.controller.is1035ExchangeEnabled
          ) {
            this.activateALIR1035Modal();
          }
        }

        break;
      case 'isReplacementOther':
        if (!this.appService.appState.controller.customer.isCoverpath2019MetadataEnabled()) {
          if (
            useNewReplacementLogic &&
            this.appService.appState.controller.customer.application.primaryPolicy.getChannelCode() === 'CAS'
          ) {
            unsupportedCase = value === 'true' ? UnsupportedCase.isReplacementOther : undefined;
          } else {
            unsupportedCase = value === 'true' ? UnsupportedCase.replacements : undefined;
          }
        }
        break;
      case 'isDOL':
        unsupportedCase = value === 'true' ? UnsupportedCase.isDOL : undefined;
        break;
      case 'isTINCorrect':
        unsupportedCase = value === 'false' ? UnsupportedCase.isTINCorrect : undefined;
        break;
      case 'beneficialInterest':
        unsupportedCase = value === 'true' ? UnsupportedCase.sell : undefined;
        break;
      case 'isNotFATCAWitholding':
        unsupportedCase = value === 'false' ? UnsupportedCase.FATCAWitholding : undefined;
        break;
      case '2018 alternate':
        //TODO placeholder until we know what property name this will be with 2018 filing - HL-5851
        unsupportedCase = value === 'true' ? UnsupportedCase.alternate : undefined;
        break;
      case '2018 tin':
        //TODO placeholder until we know what property name this will be with 2018 filing - HL-5851
        unsupportedCase = value === 'true' ? UnsupportedCase.tin : undefined;
        break;
      case 'isOwnerAlsoPayer':
        /**
         * If owner is not payor, block user from choosing to provide payment details after app submission
         * 'paymentInApplication' property exists in CoverpathPolicy and MMDirectPolicy
         */
        unsupportedCase =
          value === 'false' && castedPolicy && castedPolicy.paymentInApplication === 'later'
            ? UnsupportedCase.paymentInApplication
            : undefined;
        break;
      case 'isPilot':
        /**
         * if insured is < 14 years old and the user selects "Yes", show unsupported use case modal
         * otherwise allow the user's selection
         */
        unsupportedCase =
          value === 'true' && castedPolicy && castedPolicy.isInsuredYoungerThanAge(14)
            ? UnsupportedCase.isJuviPilot
            : undefined;
        break;
      case 'companyName':
        // Check for unsupported internal replacements (i.e. current carrier is Northwestern Mutual)
        const internalUnsupportedCompanies = ['Northwestern Mutual'];
        if (internalUnsupportedCompanies.includes(value)) {
          const existingPolicy = this.getEntityFromProp(prop); // ExistingPolicies.existingInForcePolicyList[n]
          const customerHasReplacementPolicies = Boolean(existingPolicy) && Boolean(existingPolicy.isReplacement);
          const customerHasReplacementApplications = Boolean(
            this.appService.getValue(
              'customer.application.primaryPolicy.ownerPerson.existingPolicies.replaceInforceApplication'
            )
          );

          if (customerHasReplacementPolicies || customerHasReplacementApplications) {
            unsupportedCase = UnsupportedCase.replaceNorthwesternMutual;
          }
        }
        break;
      case 'paymentInApplication':
        // If owner is not payor, block user from choosing to provide payment details after app submission
        unsupportedCase =
          value === 'later' && primaryPolicy.isOwnerAlsoPayer !== 'true'
            ? UnsupportedCase.paymentInApplication
            : undefined;
        break;
      case 'hasGirRider':
        const girPolicy = primaryPolicy as CoverpathBasePolicy;
        unsupportedCase = value === 'true' && !girPolicy.isValidGIRPolicy() ? UnsupportedCase.girRider : undefined;
        break;
      case 'hasRtrRider':
        const rtrPolicy = primaryPolicy as CoverpathBasePolicy;
        unsupportedCase = value === 'true' && !rtrPolicy.isValidRTRPolicy() ? UnsupportedCase.rtrRider : undefined;
        break;
      case 'maxFaceAmt': {
        const strippedValue = value.replace('$', '').replace(/,/g, '');
        unsupportedCase =
          this.appService.appState.controller.customer.application.getTotalFaceAmount() > parseFloat(strippedValue)
            ? UnsupportedCase.entityFaceAmountExceeded
            : undefined;
        break;
      }
      case 'ssnorein':
        unsupportedCase = value === 'noTaxId' ? UnsupportedCase.noTaxIdTrust : undefined;
        break;
      case 'prefillOwnerAddressWithInsured':
        unsupportedCase = value ? UnsupportedCase.prefillOwnerAddressWithInsured : undefined;
        break;
      case 'hasSigner':
        const insuredType: string = primaryPolicy.insuredType;
        if (
          value === 'false' &&
          Utils.exists(insuredType) &&
          !['son', 'daughter', 'grandchild', 'maleward', 'femaleward', 'notYou'].includes(insuredType)
        ) {
          unsupportedCase = UnsupportedCase.signerNotParentOrGuardian;
        }
        break;
      case 'termDisplay':
        if (value === '15' && primaryPolicy.isWholeLifePolicy()) {
          if ((primaryPolicy as CoverpathBasePolicy).hasAlirScheduledPayments) {
            unsupportedCase = UnsupportedCase.alirScheduledPaymentsWL15;
          }
        }
        break;
      case 'hasAlirLumpSum':
        unsupportedCase = value === 'true' ? UnsupportedCase.alirLumpSumAtTimeOfIssuance : undefined;
        break;
      case 'alirLumpSumUses1035':
        if (!alir1035NoPremium && value === 'true') {
          unsupportedCase = UnsupportedCase.alir1035;
        } else if (alir1035NoPremium && value === 'true' && castedPolicy.hasAlirScheduledPayments) {
          unsupportedCase = UnsupportedCase.scheduledPaymentWith1035;
        }
        break;
      case 'hasAlirScheduledPayments':
        const isWL15 = primaryPolicy.product === Product.productTypes.Whole.productCodes.CPL15;
        if (!alir1035NoPremium && value === 'false') {
          unsupportedCase = UnsupportedCase.alirWithNoScheduledPayments;
        } else if (alir1035NoPremium && value === 'true' && castedPolicy.alirLumpSumUses1035) {
          unsupportedCase = UnsupportedCase.scheduledPaymentWith1035;
        } else if (alir1035NoPremium && value === 'true' && isWL15) {
          unsupportedCase = UnsupportedCase.alirScheduledPaymentsWL15;
        }
        break;
      case 'hasRtrRiderBool':
        unsupportedCase =
          value || (await ValidationService.validateAlirAmountGeneric(value, 'hasRtrRiderBool')).result
            ? undefined
            : UnsupportedCase.invalidRtrRemoval;
        break;
      case 'hasGirRiderBool':
        if (!(await ValidationService.validateAlirAmountGeneric(value, 'hasGirRiderBool')).result) {
          unsupportedCase = UnsupportedCase.invalidGirRemoval;
        }
        break;
      case 'hasCirRider':
        if (value === true) {
          if (
            !this.appService.appState.controller.cirEnabled ||
            !CIRValidationUtils.isValidState(primaryPolicy.insured.address.state).isValid ||
            primaryPolicy.insuredIsJuvenile()
          ) {
            unsupportedCase = UnsupportedCase.genericUnsupportedMessage;
          } else if (!CIRValidationUtils.isValidFaceAmount(primaryPolicy.faceAmount).isValid) {
            unsupportedCase = UnsupportedCase.cirMinimumFaceAmount;
          }
        }
        break;
      case 'waiver':
        if (!(await ValidationService.validateAlirAmountGeneric(value, 'waiver')).result) {
          unsupportedCase = UnsupportedCase.invalidWopRemoval;
        }
        break;
      case 'alir1035Purpose':
        if (value === Exchange1035FundingPurpose.PREMIUM_AND_ALIR && !alir1035Premium) {
          unsupportedCase = UnsupportedCase.firstYearPremiumWith1035;
        } else {
          const valResult = await ValidationService.validateAlirAmountGeneric(value, 'alir1035Purpose');
          if (!valResult.result) {
            unsupportedCase = UnsupportedCase.alirCompoundValidation;
            unsupportedCaseData = this.appService.appState.isAdvisor ? valResult.error : null;
          }
        }
        break;
      case 'ownerClassification':
        unsupportedCase = value === 'another' ? UnsupportedCase.genericUnsupportedMessage : undefined;
        break;
      case 'ownerTypeSelection':
        unsupportedCase =
          value === 'grandparent' && primaryPolicy.insuredIsJuvenileDefinedSigner()
            ? UnsupportedCase.signerNotParentOrGuardian
            : undefined;
        break;
      case 'insuredType':
        unsupportedCase =
          value === 'you' && primaryPolicy.insuredIsJuvenile() ? UnsupportedCase.genericUnsupportedMessage : undefined;
        break;
      case 'hasRetirementHardshipWithdrawal':
      case 'hasRetirementWithdrawalPenalty':
        unsupportedCase = value === 'true' ? UnsupportedCase.invalidSourceOfFunds : undefined;
        break;
      case 'hasAlirRider':
        if (
          value === true &&
          castedPolicy.trustOrOtherEntity &&
          castedPolicy.trustOrOtherEntity.specificType === 'charity'
        ) {
          unsupportedCase = UnsupportedCase.alirOwnerExclusion;
        }
        break;
      case 'specificType':
        if (value === 'charity' && castedPolicy.hasAlirRider) {
          unsupportedCase = UnsupportedCase.alirOwnerExclusion;
        }
        break;
    }

    if (
      primaryPolicy.hasAlirRider &&
      ['hasAlirScheduledPayments', 'alirLumpSumUses1035', 'hasAlirLumpSum'].includes(simplePropertyName) &&
      !ValidationService.isValidAlirTypeSelection(primaryPolicy, simplePropertyName, value === 'true')
    ) {
      unsupportedCase = UnsupportedCase.alirNoOptionSelected;
    }

    if ((<any>Object).values(UnsupportedCase).includes(unsupportedCase)) {
      this.activateUnsupportedUseCasesModal(unsupportedCase, unsupportedCaseData);
      return true;
    } else {
      return false;
    }
  }

  activateUnsupportedUseCasesModal(unsupportedCase: UnsupportedCase, unsupportedCaseData?: string) {
    const modalType = {
      type: ModalType.unsupportedCases,
      unsupportedCase: unsupportedCase,
      unsupportedCaseData: unsupportedCaseData,
    };
    this.modalService.activate(modalType);
  }

  /**
   * we need to let CAS/MMD customers know if they could be eligible for LifeBridge when they are filling out the application
   */
  checkOrActivateLifeBridgeMessaging(prop: string, value: string) {
    if (!prop.match('annualIncome')) {
      return;
    }
    const isLifeBridge =
      this.appService.getValue('customer.application.primaryPolicy').amorphicGetClassName() === 'LifeBridgePolicy';
    const income = parseInt(value.replace(/\D/g, ''));
    const productSpec = Product.productSpecs.ET10;
    const shouldActivate =
      !isLifeBridge &&
      income <= productSpec.lifeBridgeNotificationIncomeMax &&
      income >= productSpec.lifeBridgeNotificationIncomeMin;

    if (shouldActivate) {
      this.activateUnsupportedUseCasesModal(UnsupportedCase.lifeBridge);
    }
  }

  private activateReplacementModal(): void {
    const modalConfig = {
      type: ModalType.policyReplacement,
    };

    this.modalService.activate(modalConfig);
  }

  private activateALIR1035Modal(): void {
    const modalConfig = { type: ModalType.ALIR1035 };
    this.modalService.activate(modalConfig);
  }

  private validateAgeForWholeLife(policy: Policy, dateOfBirth: string): UnsupportedCase {
    const product: string = policy.product;
    const faceAmount: number = this.appService.getValue('customer.application.primaryPolicy.faceAmount');
    const dob = new Date(dateOfBirth);
    const rtrAmount = policy.rtrRiderAmount || 0;
    switch (product) {
      case Product.products.CPL100.code:
      case Product.products.CPL65.code:
      case Product.products.CPL20.code:
      case Product.products.CPL15.code:
      case Product.products.CPL12.code:
      case Product.products.CPL10.code:
      case Product.products.CPL10A.code:
      case Product.products.CPL100A.code:
      case Product.products.CPL65A.code:
      case Product.products.CPL20A.code:
      case Product.products.CPL15A.code:
        if (policy.hasGirRider === 'true' && !ValidationService.isValidGirAge(dob).result) {
          return UnsupportedCase.invalidAgeForProducts;
        } else if (
          policy.hasRtrRider === 'true' &&
          !ValidationStatic.isValidRtrAge(product, faceAmount, rtrAmount, dob).result
        ) {
          return UnsupportedCase.invalidAgeForProducts;
        }
        return;
      default:
        return;
    }
  }

  private getEntityFromProp(prop: string): any {
    const stringArray = prop.split('.');
    const entity = stringArray.slice(0, stringArray.length - 1).join('.');
    return this.appService.getValue(entity);
  }

  private activateGroupReplacementModal(existingPolicy: ExistingPolicy) {
    const modalType = {
      type: ModalType.groupReplacement,
      existingPolicy: existingPolicy,
    };

    this.modalService.activate(modalType);
  }

  /**
   * This function makes sure all the fields in currenct existingPolicy are populated before showing the isGroupPolicy modal
   */
  private allFieldsCompletedForReplacementPolicy(
    existingPolicy: ExistingPolicy,
    currentField: string,
    currentValue
  ): boolean {
    const face = currentField === 'face' ? currentValue : existingPolicy.face;
    if (!face) {
      return false;
    }

    if (!existingPolicy.purpose) {
      return false;
    }

    const type: string = currentField === 'type' ? currentValue : existingPolicy.type;
    if (!type) {
      return false;
    }

    if (type === 'whole' || type === 'universal' || type === 'variable' || type === 'variable_annuity') {
      const cashValueTransfer: string =
        currentField === 'cashValueTransfer' ? currentValue : existingPolicy.cashValueTransfer;
      if (!cashValueTransfer) {
        return false;
      }

      const replacementLoanAmount =
        currentField === 'replacementLoanAmount' ? currentValue : existingPolicy.replacementLoanAmount;
      if (replacementLoanAmount !== 0 && !replacementLoanAmount) {
        return false;
      }
    }

    const companyName = currentField === 'companyName' ? currentValue : existingPolicy.companyName;
    if (!companyName) {
      return false;
    }

    if (companyName === 'Other') {
      const otherCompanyName = currentField === 'otherCompanyName' ? currentValue : existingPolicy.otherCompanyName;
      if (!otherCompanyName) {
        return false;
      }
    }

    const policyNumber = currentField === 'number' ? currentValue : existingPolicy.number;
    if (!policyNumber) {
      return false;
    }

    const insured = currentField === 'insured' ? currentValue : existingPolicy.insured;
    const insuredName = currentField === 'insuredName' ? currentValue : existingPolicy.insuredName;
    if (insured === 'other' && !insuredName) {
      return false;
    }

    const coverageStartDate = currentField === 'coverageStartDate' ? currentValue : existingPolicy.coverageStartDate;
    if (!coverageStartDate) {
      return false;
    }

    //reimbursement method is only asked when existing policy is from MassMutual
    if (companyName === 'MassMutual') {
      const reimburseMethod = currentField === 'reimburseMethod' ? currentValue : existingPolicy.reimburseMethod;
      if (!reimburseMethod) {
        return false;
      }
    }

    return true;
  }

  async validateSSN(policy: CoverpathBasePolicy, prop: string, value: string): Promise<UnsupportedCase> {
    const ownerSameAsInsured = policy.isOwnerSameAsInsured();
    const ssn = value.replace(/-/g, '');
    if (value === 'false') {
      if (prop.indexOf('owner') > 0 || ownerSameAsInsured) {
        //if owner is same as insured, show the owner modal
        return UnsupportedCase.noSSNOwner;
      } else if (prop.indexOf('insured') > 0) {
        return UnsupportedCase.noSSNInsured;
      }
    } else if (ssn.length === 9) {
      /**
       * Check that the SSN just entered doesn't conflict with the existing SSNs in the app.
       * Primary Owner can never be the Joint Owner or Contingent Owner
       * Primary Owner can sometimes be the Insured or the Juvi Signer
       * Insured can never be the Juvi Signer
       * Insured can sometimes be the Joint Owner or Contingent Owner
       */
      const insuredSSN = await this.appService.appState.controller.getPiiValueByKey(
        policy.insured.person.SSN,
        0,
        false
      );
      const signerSSN = policy.insured.signer
        ? await this.appService.appState.controller.getPiiValueByKey(policy.insured.signer.SSN, 0, false)
        : null;
      const ownerSSN = policy.ownerPerson
        ? await this.appService.appState.controller.getPiiValueByKey(policy.ownerPerson.SSN, 0, false)
        : null;
      const otherOwnerSSN = policy.otherOwner
        ? await this.appService.appState.controller.getPiiValueByKey(policy.otherOwner.SSN, 0, false)
        : null;
      const contingentOwnerSSN = policy.contingentOwnerPerson
        ? await this.appService.appState.controller.getPiiValueByKey(policy.contingentOwnerPerson.SSN, 0, false)
        : null;
      const additionalPayerSSN =
        policy.payer.constructor.name === 'Person'
          ? await this.appService.appState.controller.getPiiValueByKey((<Person>policy.payer).SSN, 0, false)
          : null;
      const policyProp = prop.split('.').slice(3).join('.');
      if (
        !ownerSameAsInsured &&
        ((policyProp === 'insured.person.SSN' && ssn === ownerSSN) ||
          (policyProp === 'ownerPerson.SSN' && ssn === insuredSSN))
      ) {
        return UnsupportedCase.ownerAndInsuredSameSSN; // Owner === Insured
      } else if (
        policy.insuredIsJuvenileHasSigner() &&
        ((policyProp === 'insured.signer.SSN' && ssn === ownerSSN) ||
          (policyProp === 'ownerPerson.SSN' && ssn === signerSSN))
      ) {
        return UnsupportedCase.ownerAndSignerSameSSN; // Owner === Juvi Signer
      } else if (
        policy.insuredIsJuvenileHasSigner() &&
        ((policyProp === 'insured.signer.SSN' && ssn === insuredSSN) ||
          (policyProp === 'insured.person.SSN' && ssn === signerSSN))
      ) {
        return UnsupportedCase.insuredAndSignerSameSSN; // Insured === Juvi Signer
      } else if (
        (policyProp === 'otherOwner.SSN' && ssn === ownerSSN) ||
        (policyProp === 'ownerPerson.SSN' && ssn === otherOwnerSSN)
      ) {
        return UnsupportedCase.ownerAndJointOwnerSameSSN; // Owner === Joint Owner
      } else if (
        (policyProp === 'contingentOwnerPerson.SSN' && ssn === ownerSSN) ||
        (policyProp === 'ownerPerson.SSN' && ssn === contingentOwnerSSN)
      ) {
        return UnsupportedCase.ownerAndContingentOwnerSameSSN; // Owner === Contingent Owner
      } else if (
        policy.premiumPayer === PayerTypeOptions.anotherPerson &&
        ((policyProp === 'payer.SSN' && ssn === ownerSSN) ||
          (policyProp === 'ownerPerson.SSN' && ssn === additionalPayerSSN))
      ) {
        return UnsupportedCase.ownerAndAdditionalPayerSameSSN; // Owner === Additional Payer
      }
    }
    return undefined;
  }

  // Parses the given string to find which position in an iterate-collect it is located
  public static getLoopIndex(prop: string, index: number = 0): number {
    const matches = prop.match(/\[\d+\]/g); // matches all numbers in brackets
    if (!matches || !matches[index]) {
      return null;
    }
    return parseInt(matches[index].match(/\d+/)[0]); // matches numbers at the index given
  }

  // Determines if any replacement other than the given iterate collect item has cash value
  private hasOtherCashValueReplacement(prop: string): boolean {
    const primaryPolicy = this.windowService.nativeWindow.controller.customer.application.primaryPolicy;
    const insuredsPolicies = primaryPolicy.insured.existingPolicies.existingInForcePolicyList;
    const ownersPolicies = primaryPolicy.insured.existingPolicies.existingPolicyList;
    const loopIndex = UnsupportedUseCasesService.getLoopIndex(prop);

    const has1035AtDifferentIndex = (replacements: ExistingPolicy[]): boolean =>
      !!replacements.find((replacement, i) => replacement.cashValueTransfer > 0 && i !== loopIndex);
    const has1035 = (replacements: ExistingPolicy[]): boolean => replacements.some((rep) => rep.cashValueTransfer > 0);

    if (prop.includes('existingInForcePolicyList')) {
      return has1035AtDifferentIndex(insuredsPolicies) || has1035(ownersPolicies);
    } else if (prop.includes('existingPolicyList')) {
      return has1035AtDifferentIndex(ownersPolicies) || has1035(insuredsPolicies);
    }
    return false;
  }

  private parseCurrency(value: string): number {
    return value ? parseFloat(value.replace(/[^0-9.\-]/g, '')) : null;
  }

  private isUnsupportedContractState(
    isTermLifePolicy: boolean,
    isCoverpathNYEnabled: boolean,
    contractState: string
  ): { isUnsupportedContractState: boolean; unsupportedKey: UnsupportedCase } {
    let coverpathUnsupportedStatesList: Array<string> = isTermLifePolicy
      ? Assumptions.coverpathUnsupportedStatesTerm
      : Assumptions.coverpathUnsupportedStatesWhole;
    // TODO HL-41004: Remove when NY is live for good. Need to account for MMD where NY is not supported
    if (isCoverpathNYEnabled) {
      coverpathUnsupportedStatesList = coverpathUnsupportedStatesList.filter((state) => state !== 'NY');
    }
    return {
      isUnsupportedContractState: coverpathUnsupportedStatesList.indexOf(contractState) >= 0,
      unsupportedKey: isTermLifePolicy ? UnsupportedCase.state : UnsupportedCase.stateWL,
    };
  }
}
